<template>
  <v-row class="toolbar-row mb-4" no-gutters>
    <TitleCounter
      v-if="config.titleCounterConfig"
      class="mr-3"
      :config="config.titleCounterConfig"
      :pro-feature="proFeature"
    />
    <ProChip v-if="showProChip" class="margin-top" />
    <v-spacer v-if="config.titleCounterConfig" />
    <DataTableSearchbar
      :no-filter="!(config.hasFilterFunctionality ?? true)"
      :table-id="dataTableId"
      @update:filter="updateFilter"
      @update:search="updateSearch"
    />
    <v-row class="mr-4 ml-2" justify="end">
      <IconButtonGroup :config="config.iconButtonConfig" />
    </v-row>
    <DataTableActionButton
      v-if="config.actionsButtonConfig"
      :data-table-id="dataTableId"
      :num-selected-elements="numSelectedElements"
      :config="config.actionsButtonConfig ?? []"
      :general-actions="$attrs['general-actions']"
      :posthogActionData="{
        dataTableId: dataTableId,
        numSelectedElements: numSelectedElements
      }"
    />
    <ThreeDotMenu
      v-if="config.actionsButtonConfig && $attrs['general-actions']"
      :config="extendedThreeDotMenuActions"
      :general-actions="true"
      :data-table-id="dataTableId"
    />
  </v-row>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import {
  Toolbar,
  ActionTypes,
  ButtonCallbacks,
  ActionsButtonWithCallback
} from '@/components/types/DataTable';
import { onPremises } from '@/shared/utils';
import { mapState } from 'pinia';
import { authenticationStore } from '@/store/pinia/AuthenticationStore';
import { downloadCSVFile } from '@/util/downloadUtil';
import { dateFormatter } from '@/util/dateFormatter';

export default defineComponent({
  name: 'DataTableToolbar',
  props: {
    dataTableId: {
      type: String,
      required: true
    },
    config: {
      type: Object as PropType<Toolbar>,
      required: true
    },
    proFeature: {
      type: Boolean,
      default: false
    },
    numSelectedElements: {
      type: Number,
      required: true
    },
    csvDataProvider: {
      type: Function as PropType<() => string>,
      required: true
    }
  },
  emits: ['update:filter', 'update:search'],
  computed: {
    ...mapState(authenticationStore, ['isProAccount']),
    showProChip(): boolean {
      return !onPremises() && this.proFeature && !this.isProAccount;
    },
    proActions(): ActionsButtonWithCallback[] {
      return [
        {
          id: ButtonCallbacks.CSV_DOWNLOAD,
          icon: 'mdi-file-download-outline',
          title: 'common.downloadCSVData',
          callback: () => this.downloadCSV(),
          types: [ActionTypes.NONE]
        }
      ];
    },
    extendedThreeDotMenuActions(): ActionsButtonWithCallback[] {
      if (this.config.disabledDownload) {
        return this.config.actionsButtonConfig ?? [];
      }
      return (this.config.actionsButtonConfig ?? []).concat(this.proActions);
    }
  },
  methods: {
    updateFilter(filter: boolean): void {
      this.$emit('update:filter', filter);
    },
    updateSearch(search: string): void {
      this.$emit('update:search', search);
    },
    downloadCSV(): void {
      const filename = this.$t('common.tableCSVFilename', {
        tableId: this.dataTableId,
        date: dateFormatter.dateForFileNames(new Date())
      });
      downloadCSVFile(this.csvDataProvider(), filename);
    }
  }
});
</script>

<style lang="scss" scoped>
.toolbar-row {
  min-width: none;
  white-space: nowrap;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  flex-direction: row !important;
  width: 100%;
}
.layout {
  max-width: fit-content;
  white-space: nowrap;
}
.margin-top {
  margin-top: 2px;
}
</style>
